import { useCallback, useEffect, useState } from 'react';
import {
  archiveFundingLender,
  createFundingLender,
  createFundingLenderProduct,
  deleteFundingLenderProduct,
  fetchFundingLender,
  fetchFundingLenders,
  updateFundingLender,
  updateFundingLenderProduct,
} from '../../services/funding/fundingLendersService';

export function useFundingLenders({ filters = {}, autoLoad = true } = {}) {
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingLenders(filters);
      setLenders(result.lenders);
      return result.lenders;
    } catch (err) {
      setError(err?.message || 'Failed to load lenders');
      setLenders([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [
    filters.lenderType,
    filters.productType,
    filters.state,
    filters.search,
    filters.status,
    filters.minCreditScore,
    filters.maxRequestedAmount,
    filters.featured,
    filters.acceptsLowCredit,
    filters.acceptsStartups,
    filters.commissionAvailable,
  ]);

  const create = useCallback(async (payload) => {
    setSaving(true);
    setError(null);
    try {
      const lender = await createFundingLender(payload);
      await load();
      return lender;
    } catch (err) {
      setError(err?.message || 'Failed to create lender');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [load]);

  const update = useCallback(async (lenderId, payload) => {
    setSaving(true);
    setError(null);
    try {
      const lender = await updateFundingLender(lenderId, payload);
      await load();
      return lender;
    } catch (err) {
      setError(err?.message || 'Failed to update lender');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [load]);

  const archive = useCallback(async (lenderId) => {
    setSaving(true);
    setError(null);
    try {
      const result = await archiveFundingLender(lenderId);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to archive lender');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [load]);

  useEffect(() => {
    if (autoLoad) load();
  }, [autoLoad, load]);

  return { lenders, loading, saving, error, load, create, update, archive };
}

export function useFundingLenderDetail({ lenderId, agencyId, autoLoad = true } = {}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!lenderId) return null;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFundingLender(lenderId, agencyId);
      setDetail(result);
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to load lender');
      setDetail(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [lenderId, agencyId]);

  const update = useCallback(async (payload) => {
    if (!lenderId) return null;
    setSaving(true);
    setError(null);
    try {
      await updateFundingLender(lenderId, payload);
      return load();
    } catch (err) {
      setError(err?.message || 'Failed to update lender');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [lenderId, load]);

  const archive = useCallback(async () => {
    if (!lenderId) return null;
    setSaving(true);
    setError(null);
    try {
      const result = await archiveFundingLender(lenderId);
      await load();
      return result;
    } catch (err) {
      setError(err?.message || 'Failed to archive lender');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [lenderId, load]);

  const addProduct = useCallback(async (payload) => {
    if (!lenderId) return null;
    setSaving(true);
    setError(null);
    try {
      await createFundingLenderProduct(lenderId, payload);
      return load();
    } catch (err) {
      setError(err?.message || 'Failed to add product');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [lenderId, load]);

  const saveProduct = useCallback(async (productId, payload) => {
    if (!lenderId || !productId) return null;
    setSaving(true);
    setError(null);
    try {
      await updateFundingLenderProduct(lenderId, productId, payload);
      return load();
    } catch (err) {
      setError(err?.message || 'Failed to update product');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [lenderId, load]);

  const removeProduct = useCallback(async (productId) => {
    if (!lenderId || !productId) return null;
    setSaving(true);
    setError(null);
    try {
      await deleteFundingLenderProduct(lenderId, productId);
      return load();
    } catch (err) {
      setError(err?.message || 'Failed to delete product');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [lenderId, load]);

  useEffect(() => {
    if (autoLoad && lenderId) load();
  }, [autoLoad, lenderId, load]);

  return {
    detail,
    lender: detail?.lender,
    performance: detail?.performance,
    loading,
    saving,
    error,
    load,
    update,
    archive,
    addProduct,
    saveProduct,
    removeProduct,
  };
}
